/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import { NLSAmpRequest, NLSAmpRateRequest } from '../services/nlsSearch.types';
import { DATES, ROOMS_AND_GUESTS, SPECIAL_RATES, USE_POINTS } from '../store/store.constants';

/**
 * Cluster codes that indicate a "Use Points / Rewards Redemption" selection.
 * Mirrors REWARDS_REDEMPTION_CLUSTER_CODES from mi-shop-components.
 */
const REWARDS_CLUSTER_CODES = new Set(['MRW', 'P17', '39E', 'MW1']);

/**
 * Cluster code the API uses for the Senior Discount rate.
 */
const SENIOR_CLUSTER_CODE = 'S9R';

/**
 * EEO (Employee) cluster codes that should be treated as Lowest Regular Rate.
 * These rates are internal/employee rates and should not populate special rates UI.
 */
const EEO_CLUSTER_CODES = new Set(['E0G', 'E0S', 'E0P']);

// ─── Mapped rate shape ────────────────────────────────────────────────────────

export interface AmpRequestSpecialRates {
  clusterCode: string;
  specialRateCode: string;
  corporateCode?: string;
  groupCode?: string;
}

export interface AmpRequestUsePoints {
  rewardsRedemption: boolean;
}

// ─── Full search-form values ──────────────────────────────────────────────────

/**
 * Derived search-form values extracted from an ampRequest payload.
 * Maps directly to the searchFormStore's DATES, ROOMS_AND_GUESTS,
 * SPECIAL_RATES, and USE_POINTS slices.
 */
export interface AmpRequestSearchFormValues {
  dates?: {
    fromDate: ReturnType<typeof moment> | null;
    toDate: ReturnType<typeof moment> | null;
    numberOfNights: number | null;
  };
  roomsAndGuests?: {
    numRooms: string;
    numAdultsPerRoom: number;
    numChildrenPerRoom: number;
    childrenAges: number[];
  };
  specialRates?: AmpRequestSpecialRates;
  usePoints?: AmpRequestUsePoints;
}

// ─── Rate mapping ─────────────────────────────────────────────────────────────

/**
 * Maps the `priceModifiers.rateRequest` array from the NLS API response to
 * the SPECIAL_RATES / USE_POINTS store slices.
 *
 * Mapping rules (derived from the actual API response examples):
 *
 *   rateRequestTypeEnum | rateRequestValue | Result
 *   ---------------------|-------------------|--------------------------------------------
 *   "Standard"           | null              | Lowest Regular Rate (clusterCode: '')
 *   "AAA"                | "aaa"             | AAA/CAA       (clusterCode: 'AAA')
 *   "GOV"                | "gov"             | Govt/Military (clusterCode: 'gov')
 *   "Cluster"            | "S9R"             | Senior Discount (clusterCode: 'S9R')
 *   "Cluster"            | P17/MRW/39E/MW1   | Use Points / Rewards → rewardsRedemption: true
 *   "Cluster"            | E0G/E0S/E0P       | EEO rates → Lowest Regular Rate (clusterCode: '')
 *   "Cluster"            | other value       | Corp/Promo Code (clusterCode: 'corp',
 *                        |                   |   corporateCode: the value)
 */
export function mapRateRequestToSpecialRates(rateRequests: NLSAmpRateRequest[] | null | undefined): {
  specialRates: AmpRequestSpecialRates;
  usePoints: AmpRequestUsePoints;
} {
  const defaultResult = {
    specialRates: { clusterCode: '', specialRateCode: 'none', corporateCode: '', groupCode: '' },
    usePoints: { rewardsRedemption: false },
  };

  if (!rateRequests || rateRequests.length === 0) return defaultResult;

  // Check for rewards/points FIRST: any entry whose cluster value is a known rewards code
  // This takes priority over EEO codes so that points searches show "Use Points/Awards"
  const hasRewardsCode = rateRequests.some(
    r => r.rateRequestTypeEnum === 'Cluster' && r.rateRequestValue && REWARDS_CLUSTER_CODES.has(r.rateRequestValue)
  );
  if (hasRewardsCode) {
    return {
      specialRates: { clusterCode: '', specialRateCode: 'none', corporateCode: '', groupCode: '' },
      usePoints: { rewardsRedemption: true },
    };
  }

  // Check for EEO (Employee) rates: treat as Lowest Regular Rate
  // Only reached if no rewards codes are present
  const hasEEOCode = rateRequests.some(
    r =>
      r.rateRequestTypeEnum === 'Cluster' &&
      r.rateRequestValue &&
      EEO_CLUSTER_CODES.has(r.rateRequestValue.toUpperCase())
  );
  if (hasEEOCode) {
    return defaultResult;
  }

  // Use the first meaningful (non-Standard) entry to determine the rate type
  const primary = rateRequests.find(r => r.rateRequestTypeEnum !== 'Standard') ?? rateRequests[0];
  const enumVal = primary.rateRequestTypeEnum?.toUpperCase() ?? '';
  const rateValue = primary.rateRequestValue ?? null;

  // AAA/CAA
  if (enumVal === 'AAA') {
    return {
      specialRates: { clusterCode: 'AAA', specialRateCode: 'aaa', corporateCode: '', groupCode: '' },
      usePoints: { rewardsRedemption: false },
    };
  }

  // Government / Military - API returns "Government" as rateRequestTypeEnum
  if (enumVal === 'GOV' || enumVal === 'GOVERNMENT') {
    return {
      specialRates: { clusterCode: 'gov', specialRateCode: 'govt', corporateCode: '', groupCode: '' },
      usePoints: { rewardsRedemption: false },
    };
  }

  // Standard → Lowest Regular Rate
  if (enumVal === 'STANDARD') {
    return defaultResult;
  }

  // Cluster codes
  if (enumVal === 'CLUSTER' && rateValue) {
    // Senior Discount
    if (rateValue.toUpperCase() === SENIOR_CLUSTER_CODE) {
      return {
        specialRates: { clusterCode: 'S9R', specialRateCode: 'senior', corporateCode: '', groupCode: '' },
        usePoints: { rewardsRedemption: false },
      };
    }

    // Corp/Promo Code — any other cluster value
    return {
      specialRates: { clusterCode: 'corp', specialRateCode: 'corp', corporateCode: rateValue, groupCode: '' },
      usePoints: { rewardsRedemption: false },
    };
  }

  return defaultResult;
}

// ─── Full ampRequest mapper ───────────────────────────────────────────────────

/**
 * Maps an `ampRequest` object from the NLS API response to the shape
 * expected by the searchFormStore slices (DATES, ROOMS_AND_GUESTS,
 * SPECIAL_RATES, USE_POINTS).
 *
 * Returns null when the ampRequest contains no usable data.
 */
export function mapAmpRequestToSearchForm(
  ampRequest: NLSAmpRequest | null | undefined
): AmpRequestSearchFormValues | null {
  const hospitality = ampRequest?.productQueriesHospitality;
  if (!hospitality) return null;

  const query = hospitality.roomStayQueryProduct?.baseHospitalityFacilityQuery;
  const result: AmpRequestSearchFormValues = {};

  // ── Dates ─────────────────────────────────────────────────────────────────
  const startDate = query?.timespan?.startDate;
  const endDate = query?.timespan?.endDate;

  if (startDate || endDate) {
    const fromDate = startDate ? moment(startDate, 'YYYY-MM-DD') : null;
    const toDate = endDate ? moment(endDate, 'YYYY-MM-DD') : null;
    const numberOfNights =
      fromDate && toDate && fromDate.isValid() && toDate.isValid() ? toDate.diff(fromDate, 'days') : null;

    result.dates = { fromDate, toDate, numberOfNights };
  }

  // ── Rooms & Guests ────────────────────────────────────────────────────────
  const guestCounts = query?.guestCounts ?? [];
  const numberOfRooms = query?.numberOfRooms;

  let numAdultsPerRoom = 0;
  let numChildrenPerRoom = 0;
  const childrenAges: number[] = [];

  for (const guest of guestCounts) {
    const count = guest.count ?? 0;
    if (guest.age === null || guest.age === undefined) {
      numAdultsPerRoom += count;
    } else {
      numChildrenPerRoom += count;
      for (let i = 0; i < count; i++) {
        childrenAges.push(guest.age);
      }
    }
  }

  if (numberOfRooms != null || numAdultsPerRoom > 0 || numChildrenPerRoom > 0) {
    result.roomsAndGuests = {
      numRooms: numberOfRooms != null ? String(numberOfRooms) : '1',
      numAdultsPerRoom: numAdultsPerRoom > 0 ? numAdultsPerRoom : 1,
      numChildrenPerRoom,
      childrenAges,
    };
  }

  // ── Special Rates ─────────────────────────────────────────────────────────
  const rateRequests = hospitality.priceModifiers?.rateRequest;
  if (rateRequests && rateRequests.length > 0) {
    const { specialRates, usePoints } = mapRateRequestToSpecialRates(rateRequests);
    result.specialRates = specialRates;
    result.usePoints = usePoints;
  }

  if (!result.dates && !result.roomsAndGuests && !result.specialRates) return null;

  return result;
}

// ─── searchQueryData builder ──────────────────────────────────────────────────

/**
 * Converts specialRates clusterCode + usePoints flag to the `rateRequestTypes`
 * array shape SearchResultsComponent expects — mirrors `constructRateTypes` in
 * mi-shop-components (submitSearchRequestHelper.ts).
 *
 * Points/rewards overrides to three entries: MRW + STANDARD + P17.
 * getCurrentPriceRateBlock only matches special-rate branch when length === 1,
 * so points results fall through to the standard-rate block — which is the
 * correct behaviour; the points display path uses pointsChecked/pointsEnable.
 */
function clusterCodeToRateRequestTypes(
  clusterCode: string,
  rewardsRedemption: boolean
): Array<{ type: string; value: string }> {
  if (rewardsRedemption) {
    return [
      { type: 'CLUSTER', value: 'MRW' },
      { type: 'STANDARD', value: '' },
      { type: 'CLUSTER', value: 'P17' },
    ];
  }
  switch (clusterCode) {
    case 's9r':
    case 'S9R':
      return [{ type: 'CLUSTER', value: clusterCode }];
    case 'gov':
      return [{ type: 'GOV', value: clusterCode }];
    case 'aaa':
    case 'AAA':
      return [{ type: 'AAA', value: clusterCode }];
    case 'corp':
      return [{ type: 'CLUSTER', value: clusterCode }];
    default:
      return [{ type: 'STANDARD', value: '' }];
  }
}

/**
 * Builds the `searchQueryData` prop for SearchResultsComponent from an
 * ampRequest payload. Seeds searchQueryState with:
 *   - options.rateRequestTypes — so getCurrentPriceRateBlock matches correctly
 *   - usePoints — so pointsChecked/sort/banner render the points UI path
 *
 * Falls back to Standard rates when no ampRequest is present.
 */
export function buildSearchQueryDataFromAmpRequest(ampRequest: NLSAmpRequest | null | undefined): {
  options: { rateRequestTypes: Array<{ type: string; value: string }> };
  usePoints: boolean;
} {
  const values = mapAmpRequestToSearchForm(ampRequest);
  const clusterCode = values?.specialRates?.clusterCode ?? '';
  const rewardsRedemption = values?.usePoints?.rewardsRedemption ?? false;
  return {
    options: { rateRequestTypes: clusterCodeToRateRequestTypes(clusterCode, rewardsRedemption) },
    usePoints: rewardsRedemption,
  };
}

/**
 * Builds the `searchQueryData` prop for SearchResultsComponent directly from
 * the live store slices (SPECIAL_RATES + USE_POINTS) set by the MiniSearchBar.
 *
 * This is preferred over buildSearchQueryDataFromAmpRequest for the MiniSearchBar
 * flow because it reflects user selections immediately — the store is updated
 * before executeSearch fires, whereas ampRequest only arrives in the response.
 *
 * Uses specialRateCode (the canonical selection key, e.g. 'aaa', 'senior', 'corp')
 * to determine rateRequestTypes, mirroring buildRateRequestTypes in nlsSearchService.
 */
export function buildSearchQueryDataFromStore(
  specialRates: AmpRequestSpecialRates | null | undefined,
  usePoints: AmpRequestUsePoints | null | undefined
): { options: { rateRequestTypes: Array<{ type: string; value: string }> }; usePoints: boolean } {
  const rewardsRedemption = usePoints?.rewardsRedemption ?? false;
  const specialRateCode = (specialRates?.specialRateCode ?? '').toLowerCase();
  const clusterCode = specialRates?.clusterCode ?? '';
  const corporateCode = specialRates?.corporateCode ?? '';

  let rateRequestTypes: Array<{ type: string; value: string }>;

  if (rewardsRedemption) {
    rateRequestTypes = [
      { type: 'CLUSTER', value: 'MRW' },
      { type: 'STANDARD', value: '' },
      { type: 'CLUSTER', value: 'P17' },
    ];
  } else if (corporateCode) {
    rateRequestTypes = [{ type: 'CLUSTER', value: corporateCode }];
  } else {
    switch (specialRateCode) {
      case 'aaa':
        rateRequestTypes = [{ type: 'AAA', value: 'aaa' }];
        break;
      case 'govt':
      case 'gov':
        rateRequestTypes = [{ type: 'GOV', value: 'gov' }];
        break;
      case 'senior':
        rateRequestTypes = [{ type: 'CLUSTER', value: 'S9R' }];
        break;
      case 'corp':
        rateRequestTypes = [{ type: 'CLUSTER', value: clusterCode || 'corp' }];
        break;
      default:
        rateRequestTypes = [{ type: 'STANDARD', value: '' }];
    }
  }

  return {
    options: { rateRequestTypes },
    usePoints: rewardsRedemption,
  };
}

/**
 * Returns the setSearchFormState call arguments needed to apply
 * ampRequest values into the searchFormStore.
 *
 * Usage:
 *   const updates = buildStoreUpdatesFromAmpRequest(ampRequest, isFlexDate);
 *   if (updates) setSearchFormState(updates.keys, updates.data);
 *
 * @param ampRequest - The ampRequest object from the NLS response
 * @param isFlexDate - Optional flag indicating if this is a flexible date search.
 *                     When provided, the `flexible` property will be set in the dates update.
 */
export function buildStoreUpdatesFromAmpRequest(
  ampRequest: NLSAmpRequest | null | undefined,
  isFlexDate?: boolean
): { keys: string[]; data: Record<string, unknown> } | null {
  const values = mapAmpRequestToSearchForm(ampRequest);
  if (!values) return null;

  const keys: string[] = [];
  const data: Record<string, unknown> = {};

  if (values.dates) {
    keys.push(DATES);
    data[DATES] = {
      fromDate: values.dates.fromDate,
      toDate: values.dates.toDate,
      numberOfNights: values.dates.numberOfNights,
      // Include flexible flag if isFlexDate is explicitly provided
      ...(isFlexDate !== undefined && { flexible: isFlexDate ? 'true' : 'false' }),
    };
  } else if (isFlexDate !== undefined) {
    // Even if no dates in ampRequest, we should still update the flexible flag
    // to ensure the MiniSearchBar shows the correct state
    keys.push(DATES);
    data[DATES] = {
      flexible: isFlexDate ? 'true' : 'false',
    };
  }

  if (values.roomsAndGuests) {
    keys.push(ROOMS_AND_GUESTS);
    data[ROOMS_AND_GUESTS] = values.roomsAndGuests;
  }

  if (values.specialRates) {
    keys.push(SPECIAL_RATES);
    data[SPECIAL_RATES] = values.specialRates;
  }

  if (values.usePoints) {
    keys.push(USE_POINTS);
    data[USE_POINTS] = values.usePoints;
  }

  return keys.length > 0 ? { keys, data } : null;
}

/**
 * Syncs search form state from NLS response data.
 * Extracts dates from llmMetadata and other fields from ampRequest,
 * then calls the provided setSearchFormState callback.
 *
 * IMPORTANT: This function should only update the flexible flag when there's
 * meaningful date information from the NLS response. If there's no date info
 * (e.g., a follow-up query that doesn't return NLS results), we preserve the
 * existing state to avoid incorrectly switching from flexible to specific dates.
 *
 * @param rawData - The raw data from the SSE node_tool_progress event
 * @param setSearchFormState - Callback to update the search form store
 * @param DATES_KEY - The key constant for dates in the store (passed to avoid import coupling)
 * @param payload - Optional payload containing isFlexDate flag from TIP
 */
export function syncSearchFormFromNLSResponse(
  rawData: any,
  setSearchFormState: (keys: string[], data: any) => void,
  DATES_KEY: string,
  payload?: any
): void {
  const nlsQuery = rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;
  const llmMetadata = nlsQuery?.llmMetadata;
  const llmDate = llmMetadata?.llmInfo?.date;
  // Only consider isFlexDate if it's explicitly set in the payload
  // undefined means TIP didn't provide date information, so we should preserve existing state
  const isFlexDateExplicitlySet = payload?.isFlexDate !== undefined;
  const isFlexDate = payload?.isFlexDate === true;

  if (llmDate?.earliest || llmDate?.latest) {
    const fromDate = llmDate.earliest ? moment(llmDate.earliest) : null;
    // For specific dates (isFlexDate=false), use the latest date as checkout
    // For flexible dates (isFlexDate=true), don't set toDate
    const toDate = isFlexDate ? null : llmDate.latest ? moment(llmDate.latest) : null;

    if (fromDate || toDate) {
      setSearchFormState([DATES_KEY], {
        [DATES_KEY]: {
          fromDate,
          toDate,
          checkInDate: llmDate.earliest || '',
          checkOutDate: isFlexDate ? '' : llmDate.latest || '',
          numberOfNights: llmDate.length || 1,
          // Explicitly set flexible based on isFlexDate from TIP response
          // This ensures switching from flexible to specific dates works correctly
          flexible: isFlexDate ? 'true' : 'false',
        },
      });
    } else if (isFlexDate) {
      // isFlexDate=true but no llmDate (date: null from BE) — still mark the
      // store as flexible so the MiniSearchBar pill shows "Flexible" instead of
      // specific dates from a previous search.
      setSearchFormState([DATES_KEY], {
        [DATES_KEY]: {
          flexible: 'true',
        },
      });
    }
  } else if (isFlexDate) {
    // isFlexDate=true but llmDate has no earliest/latest values — mark as flexible
    // This handles the case where TIP returns isFlexDate=true without specific date info
    setSearchFormState([DATES_KEY], {
      [DATES_KEY]: {
        flexible: 'true',
      },
    });
  } else if (isFlexDateExplicitlySet && !isFlexDate) {
    // Only switch to specific dates mode if TIP explicitly returned isFlexDate=false
    // This handles the case where TIP returns isFlexDate=false without date info
    // (e.g., user asks for hotels without specifying dates, but BE determines it's not flexible)
    // If isFlexDate is undefined (not in payload), we preserve the existing state
    setSearchFormState([DATES_KEY], {
      [DATES_KEY]: {
        flexible: 'false',
      },
    });
  }
  // If no llmDate and isFlexDate is undefined, preserve existing state
  // This prevents incorrectly switching from flexible to specific dates
  // when a follow-up query doesn't return NLS results

  const ampRequest = nlsQuery?.ampRequest ?? null;
  const tipStoreUpdates = buildStoreUpdatesFromAmpRequest(ampRequest);
  if (tipStoreUpdates) {
    const keysWithoutDates = tipStoreUpdates.keys.filter((k: string) => k !== DATES);
    if (keysWithoutDates.length > 0) {
      setSearchFormState(keysWithoutDates, tipStoreUpdates.data);
    }
  }
}

// ─── Availability Calendar Search Utilities ────────────────────────────────────

/**
 * CTA object structure from availability_calendar_search response
 */
export interface AvailabilityCalendarCta {
  id: string;
  propertyName?: string;
  fromDate: string;
  toDate: string;
  roomCount: number;
  guestCount: number;
  childAges: number[];
  rateRequestTypes: Array<{ type: string; value: string }>;
  rateType?: string; // DO NOT USE - use rateRequestTypes instead
  userSpecifiedDate?: boolean;
  strongBookIntent?: boolean;
  text?: string;
}

/**
 * Builds an ampRequest-like object from the CTA data in availability_calendar_search response.
 * This is used to sync the search form store when processing calendar search results.
 *
 * @param cta - The CTA object from availability_calendar_search payload
 * @returns ampRequest-like object that can be used with buildStoreUpdatesFromAmpRequest
 */
export function buildAmpRequestFromCta(cta: AvailabilityCalendarCta | null | undefined): any {
  if (!cta) return null;

  // Build guest counts array from guestCount and childAges
  const guestCounts: Array<{ count: number; age?: number }> = [];

  // Adults (guests without age)
  const childCount = cta.childAges?.length ?? 0;
  const adultCount = Math.max((cta.guestCount ?? 1) - childCount, 1);
  guestCounts.push({ count: adultCount });

  // Children (guests with age)
  if (cta.childAges && cta.childAges.length > 0) {
    for (const age of cta.childAges) {
      guestCounts.push({ count: 1, age });
    }
  }

  // Map rateRequestTypes to the format expected by mapRateRequestToSpecialRates
  const rateRequest = (cta.rateRequestTypes ?? []).map(rt => ({
    rateRequestTypeEnum: rt.type,
    rateRequestValue: rt.value || null,
  }));

  // Points / rewards scenario: when the CTA's rate types indicate a rewards
  // redemption (e.g. an MRW/P17 cluster entry), mimic the exact rateRequest shape
  // a real NLS/AMP points payload uses — the canonical three entries
  // [MRW, Standard, P17] (mirrors buildRateRequestTypes in nlsSearchService).
  // Note: the CTA emits UPPERCASE rate types (CLUSTER/STANDARD), but the AMP
  // response shape that mapRateRequestToSpecialRates consumes is title-case
  // (Cluster/Standard) — so we emit title-case here to keep the single-property
  // ampRequest consistent with a real NLS points search.
  const isPointsScenario = rateRequest.some(
    r =>
      (r.rateRequestTypeEnum === 'CLUSTER' || r.rateRequestTypeEnum === 'Cluster') &&
      r.rateRequestValue &&
      REWARDS_CLUSTER_CODES.has(r.rateRequestValue)
  );

  const finalRateRequest = isPointsScenario
    ? [
        { rateRequestTypeEnum: 'Cluster', rateRequestValue: 'MRW' },
        { rateRequestTypeEnum: 'Standard', rateRequestValue: null },
        { rateRequestTypeEnum: 'Cluster', rateRequestValue: 'P17' },
      ]
    : rateRequest;

  return {
    productQueriesHospitality: {
      roomStayQueryProduct: {
        baseHospitalityFacilityQuery: {
          timespan: {
            startDate: cta.fromDate,
            endDate: cta.toDate,
          },
          numberOfRooms: cta.roomCount ?? 1,
          guestCounts,
        },
      },
      priceModifiers: {
        rateRequest: finalRateRequest.length > 0 ? finalRateRequest : null,
      },
    },
  };
}

/**
 * Syncs search form state from availability_calendar_search CTA data.
 * Similar to syncSearchFormFromNLSResponse but uses CTA fields instead of ampRequest.
 *
 * @param cta - The CTA object from availability_calendar_search payload
 * @param setSearchFormState - Callback to update the search form store
 */
export function syncSearchFormFromCalendarCta(
  cta: AvailabilityCalendarCta | null | undefined,
  setSearchFormState: (keys: string[], data: any) => void
): void {
  if (!cta) return;

  // Sync dates from CTA
  const fromDate = cta.fromDate ? moment(cta.fromDate, 'YYYY-MM-DD') : null;
  const toDate = cta.toDate ? moment(cta.toDate, 'YYYY-MM-DD') : null;
  const numberOfNights =
    fromDate && toDate && fromDate.isValid() && toDate.isValid() ? toDate.diff(fromDate, 'days') : 1;

  // Determine if this is a flexible date search based on userSpecifiedDate from CTA
  // userSpecifiedDate: true → user specified exact dates → flexible: false
  // userSpecifiedDate: false → flexible dates → flexible: true
  // userSpecifiedDate: undefined → default to specific dates (false) for calendar search
  const isFlexDate = cta.userSpecifiedDate !== undefined ? !cta.userSpecifiedDate : false;

  if (fromDate || toDate) {
    setSearchFormState([DATES], {
      [DATES]: {
        fromDate,
        toDate,
        checkInDate: cta.fromDate || '',
        checkOutDate: cta.toDate || '',
        numberOfNights,
        flexible: isFlexDate ? 'true' : 'false',
      },
    });
  }

  // Build ampRequest from CTA and sync other fields
  const ampRequest = buildAmpRequestFromCta(cta);
  const tipStoreUpdates = buildStoreUpdatesFromAmpRequest(ampRequest);
  if (tipStoreUpdates) {
    const keysWithoutDates = tipStoreUpdates.keys.filter((k: string) => k !== DATES);
    if (keysWithoutDates.length > 0) {
      setSearchFormState(keysWithoutDates, tipStoreUpdates.data);
    }
  }
}

/**
 * Finds a property edge from previous messages in the chat session.
 * Searches through all messages with preview data to find a matching property by ID.
 *
 * @param messages - Array of messages from the chat session
 * @param propertyId - The property ID to search for
 * @returns The matching edge from a previous message, or null if not found
 */
export function findPropertyFromMessages(messages: any[], propertyId: string): any | null {
  if (!messages || !propertyId) return null;

  // Search messages in reverse (most recent first)
  for (const msg of [...messages].reverse()) {
    const edges =
      msg.previewData?.data?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges;
    if (edges && Array.isArray(edges)) {
      const match = edges.find((e: any) => e.node?.property?.id === propertyId);
      if (match) return match;
    }
  }
  return null;
}

/**
 * Finds the most recent NLS base query from the conversation's messages.
 *
 * A single-property response (availability_calendar_search) carries no NLS query
 * of its own. The MiniSearchBar / FE UXL call must therefore always reuse the NLS
 * query from the last NLS-backed message in the conversation. Resolving it from the
 * messages at call time (rather than snapshotting it) keeps the base query correct
 * across page reloads and conversation switches, where any in-memory ref would be lost.
 *
 * @param messages - Array of messages from the chat session (newest scanned first)
 * @returns The most recent base query string, or undefined if none found
 */
export function findLatestBaseQueryFromMessages(messages: any[]): string | undefined {
  if (!messages || messages.length === 0) return undefined;

  for (const msg of [...messages].reverse()) {
    const data = msg.previewData?.data;
    if (!data) continue;

    // Top-level query stored by the normal NLS handler
    const topLevelQuery: string | undefined = data.query;
    if (topLevelQuery) return topLevelQuery;

    // Fall back to llmMetadata fields from the NLS response
    const llmMetadata = data.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.llmMetadata;
    const metaQuery: string | undefined = llmMetadata?.payload?.query ?? llmMetadata?.llmInfo?.queryRewrite;
    if (metaQuery) return metaQuery;
  }

  return undefined;
}

/**
 * Transforms availability_calendar_search response to NLS-like structure.
 * This allows the existing property card rendering logic to work with calendar search results.
 *
 * @param calendarPayload - The payload from availability_calendar_search_response event
 * @param existingPropertyEdge - Property edge found from previous messages (optional)
 * @returns NLS-like rawData structure that can be used with existing rendering components
 */
export function transformCalendarResponseToNLSStructure(calendarPayload: any, existingPropertyEdge: any | null): any {
  const propertyId = calendarPayload?.property_id;
  const calendarData = calendarPayload?.rawData?.data?.search?.calendarSearchByProperty;
  const propertiesByIds = calendarPayload?.rawData?.data?.propertiesByIds;
  const cta = calendarPayload?.cta;

  // Get the lowest rate from calendar data
  let lowestRate: any = null;
  if (calendarData?.edges && calendarData.edges.length > 0) {
    // Find the edge with the lowest average rate
    for (const edge of calendarData.edges) {
      const rate = edge.node?.rateModes?.lowestAverageRate?.amount;
      if (rate && (!lowestRate || rate.amount < lowestRate.amount)) {
        lowestRate = rate;
      }
    }
  }

  // Build the property node - prefer existing property data, fall back to propertiesByIds
  let propertyNode: any;
  if (existingPropertyEdge?.node?.property) {
    // Use existing property data from previous message
    propertyNode = { ...existingPropertyEdge.node.property };
  } else if (propertiesByIds && propertiesByIds.length > 0) {
    // Fall back to limited data from propertiesByIds
    const propertyInfo = propertiesByIds[0];
    propertyNode = {
      id: propertyId,
      basicInformation: propertyInfo?.basicInformation ?? {},
    };
  } else {
    // Minimal fallback using CTA data
    propertyNode = {
      id: propertyId,
      basicInformation: {
        nameInDefaultLanguage: cta?.propertyName ?? propertyId,
      },
    };
  }

  // Build rate modes from calendar data
  const rateModes = lowestRate
    ? {
        lowestAverageRate: { amount: lowestRate },
        totalRate: calendarData?.edges?.[0]?.node?.rateModes?.totalRate ?? null,
        pointsPerQuantity: calendarData?.edges?.[0]?.node?.rateModes?.pointsPerQuantity ?? null,
        sourceOfRate: calendarData?.edges?.[0]?.node?.rateModes?.sourceOfRate ?? 'MARSHA',
      }
    : existingPropertyEdge?.node?.rateModes ?? null;

  // Build ampRequest from CTA
  const ampRequest = buildAmpRequestFromCta(cta);

  // Construct NLS-like structure
  return {
    data: {
      search: {
        conversationalSearch: {
          searchByNaturalLanguageQuery: {
            total: 1,
            edges: [
              {
                node: {
                  property: propertyNode,
                  rateModes,
                  // Preserve rates from existing edge if available
                  rates: existingPropertyEdge?.node?.rates ?? null,
                },
              },
            ],
            ampRequest,
          },
        },
      },
    },
  };
}

/**
 * Builds a chatSearchCriteria object from ampRequest and optional llmMetadata.
 * This format is used by shop components (like Rates in HQV) for URL construction.
 *
 * Use this when you need message-specific search criteria (e.g., when opening HQV
 * from a chat summary link) rather than the current global search criteria.
 *
 * @param ampRequest - The ampRequest from the NLS response
 * @param llmMetadata - Optional llmMetadata for date information
 * @param isFlexDate - Whether the search was a flexible date search
 * @returns chatSearchCriteria object or null if no usable data
 */
export function buildChatSearchCriteriaFromAmpRequest(
  ampRequest: NLSAmpRequest | null | undefined,
  llmMetadata?: any,
  isFlexDate?: boolean
): {
  checkInDate?: string;
  checkOutDate?: string;
  flexibleDate: boolean;
  lengthOfStay?: number;
  numRooms: number;
  numAdultsPerRoom: number;
  numChildrenPerRoom: number;
  childrenAges: number[];
  useRewardsPoints: boolean;
  clusterCode: string;
  corporateCode: string;
  groupCode: string;
} | null {
  const values = mapAmpRequestToSearchForm(ampRequest);

  // Extract dates from llmMetadata if available (more reliable than ampRequest for dates)
  const llmDate = llmMetadata?.llmInfo?.date;
  let checkInDate: string | undefined;
  let checkOutDate: string | undefined;
  const flexibleDate = isFlexDate ?? false;
  let lengthOfStay: number | undefined;

  if (llmDate?.earliest) {
    checkInDate = llmDate.earliest;
  } else if (values?.dates?.fromDate?.isValid()) {
    checkInDate = values.dates.fromDate.format('YYYY-MM-DD');
  }

  // For flexible dates, we still need the latest date for URL construction
  // The URL builder will use this to calculate the proper date range
  if (llmDate?.latest) {
    checkOutDate = llmDate.latest;
  } else if (values?.dates?.toDate?.isValid()) {
    checkOutDate = values.dates.toDate.format('YYYY-MM-DD');
  }

  // Capture length of stay for flexible date searches
  if (llmDate?.length) {
    lengthOfStay = llmDate.length;
  }

  // If no ampRequest data and no dates, return null
  if (!values && !checkInDate && !checkOutDate) {
    return null;
  }

  return {
    checkInDate,
    checkOutDate,
    flexibleDate,
    lengthOfStay,
    numRooms:
      values?.roomsAndGuests?.numRooms != null
        ? typeof values.roomsAndGuests.numRooms === 'string'
          ? parseInt(values.roomsAndGuests.numRooms, 10)
          : values.roomsAndGuests.numRooms
        : 1,
    numAdultsPerRoom: values?.roomsAndGuests?.numAdultsPerRoom ?? 1,
    numChildrenPerRoom: values?.roomsAndGuests?.numChildrenPerRoom ?? 0,
    childrenAges: values?.roomsAndGuests?.childrenAges ?? [],
    useRewardsPoints: values?.usePoints?.rewardsRedemption ?? false,
    clusterCode: values?.specialRates?.clusterCode ?? '',
    corporateCode: values?.specialRates?.corporateCode ?? '',
    groupCode: values?.specialRates?.groupCode ?? '',
  };
}
